import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { summariseTools } from './toolLabels';
import { splitSuggestions } from './suggestions';
import { extractChart } from './chartSpec';
import { extractDraft } from './draft';
import { LivyChart } from './LivyChart';
import { LivyMarkdown } from './LivyMarkdown';
import { LivyProposal, findProposalIds } from './LivyProposal';
import { LivyRequestCard, findRequestIds } from './LivyRequestCard';
import { CARET, EXCHANGE_START, PILL, TOOL_CHIP, USER_BUBBLE } from './styles';
import { LivyAvatar } from './LivyAvatar';
import { LivyFeedback } from './LivyFeedback';
const MAX_TOOL_CHIPS = 4;

/** Secondary to the answer: available when wanted, invisible when not. */
const ACTION_BUTTON = {
  minWidth: 0,
  px: '6px',
  py: '2px',
  fontSize: 12,
  fontWeight: 400,
  textTransform: 'none',
  color: 'text.secondary',
  // Declared background as well as colour. An element that names only one of the pair renders
  // unreadably under an inverting dark-mode extension, which is how Copy and Retry became two
  // blank boxes.
  bgcolor: 'background.paper',
  '&:hover': {
    color: 'text.primary',
    bgcolor: 'action.hover'
  }
};

/**
 * The user gets a bubble; the assistant does not. An answer that sits on the page reads as the
 * product talking, and lets a table or a chart use the full column instead of being boxed inside a
 * speech bubble.
 */
export function LivyMessage(_ref) {
  var _message$tools, _message$requestIds;
  let message = _ref.message,
    _ref$streaming = _ref.streaming,
    streaming = _ref$streaming === void 0 ? false : _ref$streaming,
    conversationId = _ref.conversationId,
    turnId = _ref.turnId,
    surface = _ref.surface,
    startsExchange = _ref.startsExchange,
    onRetry = _ref.onRetry,
    onSuggestion = _ref.onSuggestion;
  if (message.role === 'user') {
    return /*#__PURE__*/React.createElement(Box, {
      sx: _objectSpread({
        display: 'flex',
        justifyContent: 'flex-end',
        mb: '22px'
      }, startsExchange ? EXCHANGE_START : {})
    }, /*#__PURE__*/React.createElement(Box, {
      sx: USER_BUBBLE
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      sx: {
        fontSize: 14,
        lineHeight: 1.5
      }
    }, message.content)));
  }
  const _splitSuggestions = splitSuggestions(message.content),
    afterSuggestions = _splitSuggestions.body,
    suggestions = _splitSuggestions.suggestions;
  const _extractDraft = extractDraft(afterSuggestions),
    afterDraft = _extractDraft.body,
    draft = _extractDraft.draft;
  const _extractChart = extractChart(afterDraft),
    body = _extractChart.body,
    charts = _extractChart.charts;
  const used = summariseTools((_message$tools = message.tools) !== null && _message$tools !== void 0 ? _message$tools : []);
  const shown = used.slice(0, MAX_TOOL_CHIPS);
  // An answer that has produced nothing yet still needs to look alive.
  const pending = streaming && !body && charts.length === 0;
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'flex',
      gap: '12px',
      mb: '28px'
    }
  }, /*#__PURE__*/React.createElement(LivyAvatar, {
    thinking: streaming
  }), /*#__PURE__*/React.createElement(Box, {
    sx: {
      minWidth: 0,
      flex: 1
    }
  }, used.length > 0 && /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "6px",
    sx: {
      flexWrap: 'wrap',
      rowGap: '4px',
      mb: '10px',
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 11,
      color: 'text.secondary',
      mr: '2px'
    }
  }, "Based on"), shown.map(_ref2 => {
    let label = _ref2.label,
      count = _ref2.count;
    return (
      /*#__PURE__*/
      // A box, not a Chip, for the same reason the suggestion pills are buttons.
      React.createElement(Box, {
        key: label,
        sx: TOOL_CHIP
      }, count > 1 ? "".concat(label, " \xD7").concat(count) : label)
    );
  }), used.length > shown.length && /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      alignSelf: 'center'
    }
  }, "+", used.length - shown.length, " more")), pending ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "text.secondary",
    sx: {
      fontSize: 14
    }
  }, "Working on it", /*#__PURE__*/React.createElement(Box, {
    component: "span",
    sx: CARET
  })) : /*#__PURE__*/React.createElement(Box, {
    sx: {
      fontSize: 15,
      lineHeight: 1.65
    }
  }, /*#__PURE__*/React.createElement(LivyMarkdown, {
    content: body
  }), streaming && /*#__PURE__*/React.createElement(Box, {
    component: "span",
    sx: CARET
  })), charts.map((spec, i) => /*#__PURE__*/React.createElement(LivyChart, {
    key: i,
    spec: spec
  })), !streaming && findProposalIds(body).map(id => /*#__PURE__*/React.createElement(LivyProposal, {
    key: id,
    proposalId: id
  })), !streaming && [...new Set([...((_message$requestIds = message.requestIds) !== null && _message$requestIds !== void 0 ? _message$requestIds : []), ...findRequestIds(body)])].map(id => /*#__PURE__*/React.createElement(LivyRequestCard, {
    key: id,
    requestId: id
  })), draft && /*#__PURE__*/React.createElement(Box, {
    sx: {
      mt: '12px',
      p: '12px 14px',
      borderRadius: '10px',
      border: 1,
      borderColor: 'divider',
      borderLeftWidth: 3,
      borderLeftColor: 'primary.main',
      bgcolor: 'action.hover'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    sx: {
      fontSize: 10.5,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '6px'
    }
  }, "Draft reply, not sent"), /*#__PURE__*/React.createElement(LivyMarkdown, {
    content: draft
  })), !streaming && body && /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "4px",
    sx: {
      mt: '10px',
      ml: '-6px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: () => void navigator.clipboard.writeText(body),
    sx: ACTION_BUTTON
  }, "Copy"), onRetry && /*#__PURE__*/React.createElement(Button, {
    size: "small",
    onClick: onRetry,
    sx: ACTION_BUTTON
  }, "Retry"), conversationId && turnId && /*#__PURE__*/React.createElement(LivyFeedback, {
    conversationId: conversationId,
    turnId: turnId,
    surface: surface !== null && surface !== void 0 ? surface : 'admin',
    tools: message.tools
  })), suggestions.length > 0 && !streaming && /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    sx: {
      flexWrap: 'wrap',
      rowGap: '8px',
      mt: '14px'
    }
  }, suggestions.map(s => /*#__PURE__*/React.createElement(Button, {
    key: s,
    disableRipple: true,
    onClick: onSuggestion ? () => onSuggestion(s) : undefined,
    sx: PILL
  }, s)))));
}